import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconDeviceFloppy,
  IconInfoCircle,
  IconRadar,
} from '@tabler/icons-react'

import { ValveRecoveryPolicy } from '@data/device/model/device'

import {
  RECOVERY_POLICY_OPTIONS,
  RecoveryModeHelp,
  ValveProbeHelp,
} from './DeviceValvesTab'

export interface ValveRecoveryPanelProps {
  selectedPolicy: ValveRecoveryPolicy | null
  isPolicyModified: boolean
  isSavingPolicy: boolean
  onPolicyChange: (value: string | null) => void
  onDiscardPolicy: () => void
  onSavePolicy: () => void
  probeConfirming: boolean
  onProbeConfirmingChange: (value: boolean) => void
  onProbe: () => void
  isProbing: boolean
  isFirmwareSupported: boolean
  minFirmwareVersion: string
}

// Shared by the Device Modal's Valves tab and the standalone Operate Valves page — see
// useValveRecoveryPolicy for the state/handlers this renders.
export const ValveRecoveryPanel = ({
  selectedPolicy,
  isPolicyModified,
  isSavingPolicy,
  onPolicyChange,
  onDiscardPolicy,
  onSavePolicy,
  probeConfirming,
  onProbeConfirmingChange,
  onProbe,
  isProbing,
  isFirmwareSupported,
  minFirmwareVersion,
}: ValveRecoveryPanelProps) => (
  <Card withBorder p='md'>
    <Stack gap='xs'>
      <Group justify='space-between' wrap='nowrap'>
        <Group gap={6} wrap='nowrap'>
          <Text fw={600} size='md'>
            Valve Recovery Mode
          </Text>

          <RecoveryModeHelp />
        </Group>

        {isPolicyModified && (
          <Badge color='yellow' variant='light' size='sm'>
            Unsaved
          </Badge>
        )}
      </Group>

      {!isFirmwareSupported && (
        <Alert
          color='gray'
          variant='light'
          icon={<IconInfoCircle size={16} />}
        >
          Requires firmware {minFirmwareVersion} or later. Update the device firmware to
          enable valve recovery mode and the valve probe.
        </Alert>
      )}

      <Select
        description='Applies to every valve on this device.'
        placeholder='Unknown'
        data={RECOVERY_POLICY_OPTIONS}
        value={selectedPolicy !== null ? String(selectedPolicy) : null}
        onChange={onPolicyChange}
        disabled={isSavingPolicy || !isFirmwareSupported}
      />

      <Group justify='flex-end' gap='sm'>
        <Button
          variant='default'
          size='xs'
          onClick={onDiscardPolicy}
          disabled={!isPolicyModified || isSavingPolicy}
        >
          Discard
        </Button>

        <Button
          size='xs'
          onClick={onSavePolicy}
          disabled={!isPolicyModified || !isFirmwareSupported}
          loading={isSavingPolicy}
          leftSection={<IconDeviceFloppy size={14} />}
        >
          Save recovery mode
        </Button>
      </Group>

      <Divider my='xs' />

      <Group gap={6} wrap='nowrap'>
        <Text fw={600} size='md'>
          Valve Probe
        </Text>

        <ValveProbeHelp />
      </Group>

      <Text size='sm' c='dimmed'>
        Re-reads every valve's physical position by re-energising its drive line. Use
        it when a valve reports an indeterminate position or has failed to operate.
      </Text>

      {probeConfirming ? (
        <Alert
          color='orange'
          variant='light'
          icon={<IconAlertTriangle size={18} />}
        >
          <Stack gap='sm'>
            <Text size='sm'>
              This energises the drive lines and <b>can physically move a valve</b>. The
              device is blocked for up to 9 seconds. Continue?
            </Text>

            <Group gap='sm'>
              <Button
                size='xs'
                color='orange'
                onClick={onProbe}
                loading={isProbing}
              >
                Run probe
              </Button>

              <Button
                size='xs'
                variant='default'
                onClick={() => onProbeConfirmingChange(false)}
                disabled={isProbing}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Alert>
      ) : (
        <Group justify='flex-end'>
          <Button
            size='xs'
            variant='light'
            color='orange'
            onClick={() => onProbeConfirmingChange(true)}
            loading={isProbing}
            disabled={!isFirmwareSupported}
            leftSection={<IconRadar size={14} />}
          >
            Run valve probe
          </Button>
        </Group>
      )}
    </Stack>
  </Card>
)
